import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const SkeletonBox = ({ className = '' }) => (
  <div className={`rounded animate-pulse ${className}`} style={{ backgroundColor: '#1f1f1f' }} />
);

const fmtM = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000)?.toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000)?.toFixed(1) + 'K';
  return String(Math.round(n));
};

const ServerCard = ({ server, currentDiamonds, target, color, paliers, loading, t }) => {
  const pct = target > 0 && currentDiamonds != null ? (currentDiamonds / target) * 100 : null;

  // FIX 2: Use completion_tiers style paliers (objects with pct property)
  const currentPalier = useMemo(() => {
    if (pct == null || !paliers?.length) return null;
    return paliers?.filter(p => pct >= (p?.pct || p?.palier_pct || 0))?.at(-1) || null;
  }, [pct, paliers]);

  const nextPalier = useMemo(() => {
    if (!paliers?.length) return null;
    return paliers?.find(p => pct == null || pct < (p?.pct || p?.palier_pct || 0)) || null;
  }, [pct, paliers]);

  const barPct = pct != null ? Math.min((pct / 130) * 100, 100) : 0;

  const diamandsManquants = useMemo(() => {
    if (!nextPalier || currentDiamonds == null || target <= 0) return null;
    const nextPct = nextPalier?.pct || nextPalier?.palier_pct || 0;
    return Math.max(0, Math.round((nextPct / 100) * target) - currentDiamonds);
  }, [nextPalier, currentDiamonds, target]);

  const nextPctVal = nextPalier?.pct || nextPalier?.palier_pct || 0;
  const nextBonusVal = nextPalier?.bonus || nextPalier?.bonus_pct || 0;
  const curPctVal = currentPalier?.pct || currentPalier?.palier_pct || 0;
  const curBonusVal = currentPalier?.bonus || currentPalier?.bonus_pct || 0;

  if (loading) {
    return (
      <div
        className="rounded-xl p-4 border flex-1 min-w-[200px]"
        style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
      >
        <SkeletonBox className="h-4 w-16 mb-3" />
        <SkeletonBox className="h-5 w-32 mb-3" />
        <SkeletonBox className="h-2 w-full mb-3" />
        <SkeletonBox className="h-3 w-24" />
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-4 border flex-1 min-w-[200px]"
      style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-md"
          style={{ backgroundColor: `${color}22`, color, fontFamily: 'Sora, sans-serif' }}
        >
          {server}
        </span>
        <span
          className="text-xs font-semibold"
          style={{
            color: pct != null ? color : 'rgba(255,255,255,0.3)',
            fontFamily: 'Sora, sans-serif',
          }}
        >
          {pct != null ? pct?.toFixed(1) + '%' : '—'}
        </span>
      </div>
      <p
        className="text-sm font-bold mb-3"
        style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Sora, sans-serif' }}
      >
        💎 {fmtM(currentDiamonds)} / {fmtM(target)}
      </p>
      <div className="w-full h-2 rounded-full overflow-hidden mb-3" style={{ backgroundColor: '#1f1f1f' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${barPct}%`, backgroundColor: color }}
        />
      </div>
      <div className="mb-2">
        {currentPalier ? (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md"
            style={{ backgroundColor: '#22c55e18', color: '#22c55e', fontFamily: 'Inter, sans-serif' }}
          >
            {t('admin.performanceActuelle.currentPalier', { pct: curPctVal, bonus: curBonusVal })}
          </span>
        ) : (
          <span
            className="text-xs"
            style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}
          >
            {t('admin.performanceActuelle.noPalier')}
          </span>
        )}
      </div>
      {nextPalier && diamandsManquants != null && (
        <p className="text-xs" style={{ color: '#f97316', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.nextPalier', { pct: nextPctVal, missing: diamandsManquants?.toLocaleString('fr-FR'), bonus: nextBonusVal })}
        </p>
      )}
      {!nextPalier && pct != null && (
        <p className="text-xs font-semibold" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.allPaliersReached')}
        </p>
      )}
    </div>
  );
};

const ServerObjectivesSection = ({
  serversData,
  selectedServer,
  currentData,
  targets,
  paliersMap,
  fallbackPaliers,
  loading,
}) => {
  const { t } = useTranslation();
  // Filter servers to show only selected server when not Tous — compare by id only
  const serversToShow = useMemo(() => {
    if (selectedServer === 'Tous') return serversData || [];
    return (serversData || [])?.filter((s) => s?.id === selectedServer);
  }, [serversData, selectedServer]);

  // Compute realized per server using server id (not name)
  const serverDiamonds = useMemo(() => {
    const map = {};
    currentData?.forEach((r) => {
      const key = r?.server;
      if (!key) return;
      if (!map?.[key]) map[key] = 0;
      map[key] += r?.diamonds || 0;
    });
    return map;
  }, [currentData]);

  if (!loading && !serversToShow?.length) {
    return (
      <div
        className="rounded-xl p-5 border flex items-center justify-center h-24"
        style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
      >
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.noActiveServer')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-4">
      {loading
        ? [1, 2, 3, 4]?.map((i) => (
            <ServerCard key={i} server="—" currentDiamonds={0} target={0} color="#E5202A" paliers={[]} loading t={t} />
          ))
        : serversToShow?.map((srv) => {
            // targets and paliers keyed by server id
            const serverId = srv?.id;
            const realized = serverDiamonds?.[serverId] || 0;
            const target = targets?.[serverId] || 0;
            const paliers = paliersMap?.[serverId]?.length > 0
              ? paliersMap?.[serverId]
              : fallbackPaliers;
            return (
              <ServerCard
                key={srv?.id}
                server={srv?.name}
                currentDiamonds={realized}
                target={target}
                color={srv?.color || '#E5202A'}
                paliers={paliers}
                loading={false}
                t={t}
              />
            );
          })}
    </div>
  );
};

export default ServerObjectivesSection;
